import type { IconProps, Icon as PhosphorIcon } from "@phosphor-icons/react";
import { forwardRef, type ReactNode } from "react";

/**
 * Factory for Texture-authored icons that are NOT available in Phosphor but
 * must plug into the Edges `<Icon name="…" />` component with the exact same
 * API (size / color / weight / className / mirrored / aria-*).
 *
 * Each custom icon is authored as a Phosphor-compatible component:
 *  - 256×256 viewBox (matches Phosphor's optical grid so custom + stock icons
 *    sit at the same scale)
 *  - `fill={color}` defaulting to `currentColor` (inherits text color like Phosphor)
 *  - `size` drives both width and height
 *
 * `weight` is accepted for API parity but is a no-op for single-weight custom
 * glyphs. Author the `children` against the 256×256 grid.
 *
 * @example
 * ```tsx
 * export const TextureRecloserIcon = createTextureIcon(
 *   "TextureRecloser",
 *   <path d="M48 128 ..." />,
 * );
 * ```
 */
export function createTextureIcon(displayName: string, children: ReactNode): PhosphorIcon {
  const Component = forwardRef<SVGSVGElement, IconProps>(
    ({ size = 24, color = "currentColor", className, mirrored, alt, weight: _weight, ...rest }, ref) => {
      // The Edges `Icon` wrapper always layers on an accessible name via
      // `aria-label` (spread through `rest`). Treat the glyph as decorative
      // ONLY when neither an explicit `alt` nor an inherited `aria-label`
      // is present — otherwise a hard-coded `aria-hidden` would silence the
      // very label the wrapper just set, hiding custom glyphs from SR users.
      const hasAccessibleName = Boolean(alt) || "aria-label" in rest;
      return (
        <svg
          ref={ref}
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 256 256"
          fill={color}
          color={color}
          className={className}
          transform={mirrored ? "scale(-1, 1)" : undefined}
          aria-hidden={hasAccessibleName ? undefined : true}
          role={hasAccessibleName ? "img" : undefined}
          aria-label={alt}
          {...rest}
        >
          {children}
        </svg>
      );
    }
  );

  Component.displayName = displayName;
  // Custom icons are single-weight; the cast aligns the factory output with the
  // Phosphor `Icon` component type so both resolve uniformly in the registry.
  return Component as unknown as PhosphorIcon;
}
